import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LanguageIcon from '@mui/icons-material/Language';

export default function LanguageSelectionModal({
  open,
  onClose,
  currentLanguage,
  availableLanguages,
  onSelectLanguage
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredLanguages = useMemo(() => {
    return availableLanguages.filter(lang => 
      lang.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      lang.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [availableLanguages, searchQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon sx={{ color: '#0ea5e9' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Select Language
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <TextField
          fullWidth
          placeholder="Search language..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#cbd5e1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0ea5e9',
                borderWidth: '1px',
              },
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />

        <List sx={{ p: 0, maxHeight: '400px', overflowY: 'auto' }}>
          {filteredLanguages.map((lang) => {
            const isSelected = currentLanguage === lang.code;
            return (
              <ListItem key={lang.code} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => {
                    onSelectLanguage(lang.code);
                  }}
                  sx={{
                    borderRadius: '12px',
                    p: 1.5,
                    border: isSelected ? '1px solid #bae6fd' : '1px solid transparent',
                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#e0f2fe' : '#f8fafc',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s',
                  }}
                >
                  <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                    {isSelected ? (
                      <CheckCircleIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
                    ) : null}
                  </Box>

                  <Box sx={{ fontSize: '1.25rem', display: 'flex' }}>
                    {lang.flag}
                  </Box>

                  <Typography
                    sx={{
                      flex: 1,
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? '#0f172a' : '#334155',
                    }}
                  >
                    {lang.name} <span style={{ color: '#94a3b8', marginLeft: 4 }}>({lang.code.toUpperCase()})</span>
                  </Typography>

                  {isSelected && (
                    <CheckIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
}
